import React, { useState } from 'react';
import ExpandSection from 'src/components/ExpandSection';
import styled from 'styled-components';
import { LogSwitch } from 'src/components/charts/comps';
import { Spacer } from 'src/common';
import ChartBlank from 'src/components/charts/ChartBlank';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { LegendComposed, VersionedChart } from './comps';
import { AssessmentQuery, CompletionState } from 'src/graphql/_generated';
import { checkChartIsBaseline } from 'src/utils/misc';

const Div = styled.div`
  .exceedance {
    display: flex;
    align-items: center;
    justify-content: space-between;
    &__end {
      display: flex;
      align-items: center;
      grid-gap: 30px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .exceedance {
      &__end {
        grid-gap: 10px;
      }
    }
  }
`;

interface ExceedanceSectionProps {
  data: NonNullable<AssessmentQuery['assessment']>;
  showAllProjected: boolean;
  isLogarithmic: boolean;
  setIsLogarithmic: (isLogarithmic: boolean) => void;
  version?: string;
}

export const ExceedanceSection: React.FC<ExceedanceSectionProps> = ({
  data,
  showAllProjected,
  version,
  isLogarithmic,
  setIsLogarithmic,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMd = useBreakpoints('md');
  const isChartBaseline = checkChartIsBaseline(data?.AssessmentLiveRisk.chart);
  const allScenariosIncomplete = data?.scenarios.every(
    (el) => el.isIncomplete === CompletionState.Incomplete || el.isIncomplete === CompletionState.IncompleteEmptyVar,
  );
  const isLegacyVersion = !!data.versions.find((el) => el.id === version)?.legacyVersion;

  return (
    <ExpandSection
      title="LOSS EXCEEDANCE CURVE"
      sectionId="assessment_exceedance"
      onChange={setIsOpen}
      endComponent={
        <Div
          style={{
            display: !version && isOpen ? 'block' : 'none',
          }}
        >
          <div className="exceedance">
            <div className="exceedance__end">
              {!!data?.scenarios.length && !isMd && (
                <LegendComposed isLegacyVersion={isLegacyVersion} showAllProjected={showAllProjected} />
              )}
              <LogSwitch skey="log:assessment" isLogarithmic={isLogarithmic} setIsLogarithmic={setIsLogarithmic} />
            </div>
          </div>
        </Div>
      }
    >
      <Div>
        {!!data?.scenarios.length && isMd && (
          <LegendComposed isLegacyVersion={isLegacyVersion} showAllProjected={showAllProjected} />
        )}
        <Spacer $px={20} />
        {!isChartBaseline && !allScenariosIncomplete ? (
          <VersionedChart
            isLogarithmic={isLogarithmic}
            isLegacyVersion={isLegacyVersion}
            showAllProjected={showAllProjected}
            base={data.AssessmentBaseRisk}
            live={data.AssessmentLiveRisk}
            projected={data.AssessmentProjectedRisk}
            riskTolerance={data.riskTolerance}
            scenarios={data.scenarios}
          />
        ) : (
          <ChartBlank techIncomplete />
        )}
      </Div>
    </ExpandSection>
  );
};
